package ai.wagemix.logic.mixbuilder

import ai.wagemix.WeightData
import ai.wagemix.data.WMCache
import ai.wagemix.data.create
import ai.wagemix.data.isOwnedBy
import ai.wagemix.data.statsSignature
import ai.wagemix.logic.WMMixDataGateway
import ai.wagemix.logic.session
import ai.wagemix.net.client.WMOccupationStatsCollectionRequestBuilder
import ai.wagemix.net.models.WMMixFilters
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.firebase.WMFirebaseMix
import ai.wagemix.net.models.firebase.kxs
import ai.wagemix.net.models.of
import ai.wagemix.recalculateWeightsGivenDisabledItem
import ai.wagemix.recalculateWeightsGivenEnabledItem
import ai.wagemix.recalculateWeightsGivenWeightChange
import kotlin.math.abs

/*
 * The draft arithmetic: the bridge between the cached MixDraft and the WeightUtils weight space
 * (each enabled occupation contributes 100 units of weight space; a stored mix records each
 * occupation's share of the whole as a percentage).
 */

internal const val WEIGHT_UNIT = 100.0

/**
 * The draft as the signed-in user (or the logged-out visitor) sees it: a draft written for anyone
 * else is absent.
 */
internal suspend fun WMMixDataGateway.retrieveOwnMixDraft(): WMCache.Value.MixDraft? {
    val uid = session()?.uid
    return retrieveMixDraft()?.takeIf { it.isOwnedBy(uid) }
}

/**
 * Read-modify-write of the user's own draft: [modify] sees `null` for someone else's, and the
 * result is recorded as the user's.
 */
internal suspend fun WMMixDataGateway.modifyOwnMixDraft(modify: (WMCache.Value.MixDraft?) -> WMCache.Value.MixDraft?) {
    val uid = session()?.uid
    modifyMixDraft { current -> modify(current?.takeIf { it.isOwnedBy(uid) })?.ownedBy(uid) }
}

internal fun WMCache.Value.MixDraft.toWeightMap(): Map<Int, WeightData> = occupations.associate {
    it.id to WeightData(itemId = it.id, weight = it.weight.toFloat(), isLocked = it.locked, isEnabled = it.enabled)
}

internal fun WMCache.Value.MixDraft.withWeights(map: Map<Int, WeightData>): WMCache.Value.MixDraft {
    val order = occupations.map { it.id } + map.keys.filter { id -> occupations.none { it.id == id } }
    return WMCache.Value.MixDraft.create(
        occupations = order.mapNotNull { id ->
            map[id]?.let { WMCache.Value.DraftOccupation.create(id = id, weight = it.weight.toDouble(), locked = it.isLocked, enabled = it.isEnabled) }
        },
        areaId = areaId,
        industryId = industryId,
        sourceMixId = sourceMixId,
        title = title,
        ownerUid = ownerUid
    )
}

/** The total weight space: 100 per enabled occupation. */
internal fun WMCache.Value.MixDraft.weightSpace(): Double = occupations.count { it.enabled } * WEIGHT_UNIT

/**
 * An occupation's share of the mix, 0..1. Disabled occupations have no share, and neither do the [excluded] ones (the
 * stats have no data for them): the other enabled occupations divide their weight in proportion to their own.
 */
internal fun WMCache.Value.MixDraft.shareOf(
    occupation: WMCache.Value.DraftOccupation,
    excluded: Set<Int> = emptySet()
): Double {
    if (!occupation.enabled || occupation.id in excluded) {
        return 0.0
    }
    val space = countedSpace(excluded)
    return if (space <= 0.0) 0.0 else (occupation.weight / space).coerceIn(0.0, 1.0)
}

/** The weight of the enabled occupations that are not [excluded]: what their shares divide. */
private fun WMCache.Value.MixDraft.countedSpace(excluded: Set<Int>): Double {
    return weightSpace() - occupations.filter { it.enabled && it.id in excluded }.sumOf { it.weight }
}

internal fun WMCache.Value.MixDraft.adding(occupationId: Int): WMCache.Value.MixDraft {
    return withWeights(recalculateWeightsGivenEnabledItem(occupationId, toWeightMap()))
}

internal fun WMCache.Value.MixDraft.enabling(occupationId: Int): WMCache.Value.MixDraft {
    return withWeights(recalculateWeightsGivenEnabledItem(occupationId, toWeightMap()))
}

internal fun WMCache.Value.MixDraft.disabling(occupationId: Int): WMCache.Value.MixDraft {
    return withWeights(recalculateWeightsGivenDisabledItem(occupationId, remove = false, toWeightMap()))
}

internal fun WMCache.Value.MixDraft.removing(occupationId: Int): WMCache.Value.MixDraft {
    if (occupations.size <= 1) {
        return WMCache.Value.MixDraft.create(emptyList(), areaId, industryId, sourceMixId, title, ownerUid)
    }
    return withWeights(recalculateWeightsGivenDisabledItem(occupationId, remove = true, toWeightMap()))
}

internal fun WMCache.Value.MixDraft.togglingLock(occupationId: Int): WMCache.Value.MixDraft {
    val map = toWeightMap().toMutableMap()
    val current = map[occupationId] ?: return this
    if (!current.isEnabled) {
        return this
    }
    map[occupationId] = if (current.isLocked) current.unlocked() else current.locked()
    return withWeights(map)
}

/**
 * [share] is the new share of the mix, 0..1, as [shareOf] gives it with the same [excluded] occupations. They keep
 * their weight while the others are rebalanced, so the occupation lands exactly on [share]; an excluded occupation's
 * own share cannot be set.
 */
internal fun WMCache.Value.MixDraft.settingShare(
    occupationId: Int,
    share: Double,
    excluded: Set<Int> = emptySet()
): WMCache.Value.MixDraft {
    if (occupationId in excluded) {
        return this
    }
    val map = toWeightMap().toMutableMap()
    val held = excluded.mapNotNull { id -> map[id]?.takeIf { it.isEnabled && it.isUnlocked }?.let { id to it } }
    held.forEach { (id, data) -> map[id] = data.copy(isLocked = true) }
    val weight = share.coerceIn(0.0, 1.0) * countedSpace(excluded)
    recalculateWeightsGivenWeightChange(occupationId, weight.toFloat(), map)
    held.forEach { (id, _) -> map[id]?.let { map[id] = it.copy(isLocked = false) } }
    return withWeights(map)
}

/**
 * The area and industry a draft keeps when [areaId] and [industryId] are asked for: a mix filters by an area or by an
 * industry, never both. When both are given, the industry wins if it differs from this draft's, otherwise the area
 * does, and the other is cleared.
 */
internal fun WMCache.Value.MixDraft?.exclusiveFilters(areaId: Int?, industryId: Int?): Pair<Int?, Int?> {
    if (areaId == null || industryId == null) {
        return areaId to industryId
    }
    return if (industryId != this?.industryId) null to industryId else areaId to null
}

internal fun WMCache.Value.MixDraft.withFilters(areaId: Int?, industryId: Int?): WMCache.Value.MixDraft {
    return WMCache.Value.MixDraft.create(occupations, areaId, industryId, sourceMixId, title, ownerUid)
}

internal fun WMCache.Value.MixDraft.withSource(sourceMixId: String?, title: String?): WMCache.Value.MixDraft {
    return WMCache.Value.MixDraft.create(occupations, areaId, industryId, sourceMixId, title, ownerUid)
}

/** The same draft recorded as belonging to [uid] (`null` = logged out). */
internal fun WMCache.Value.MixDraft.ownedBy(uid: String?): WMCache.Value.MixDraft {
    return WMCache.Value.MixDraft.create(occupations, areaId, industryId, sourceMixId, title, uid)
}

/** The occupation id of the taxonomy root, "All occupations" (SOC 00-0000). */
internal const val ALL_OCCUPATIONS_ID = 1

/** The draft every user starts from: "All occupations" alone, owned by [ownerUid]. */
internal fun WMCache.Value.MixDraft.Companion.createDefault(ownerUid: String?): WMCache.Value.MixDraft {
    return WMCache.Value.MixDraft.create(emptyList(), ownerUid = ownerUid).adding(ALL_OCCUPATIONS_ID)
}

/**
 * Shapes the API's stats request for this draft; the caller passes it to `syncStats`. Every
 * occupation is requested with weight 1, enabled or not, so the fetch depends only on which
 * occupations and filters the draft has: moving a slider or toggling an occupation re-weights
 * the fetched collection locally ([shares]) and never reaches the network.
 */
internal fun WMCache.Value.MixDraft.statsRequest(): WMOccupationStatsCollectionRequestBuilder.() -> Unit {
    val ids = occupations.map { it.id }.distinct()
    val area = areaId
    val industry = industryId
    return {
        area?.let { areaId(it) }
        industry?.let { industryId(it) }
        ids.forEach { addOccupationId(it, 1) }
    }
}

/** The key the draft's stats live under in the global context, or `null` when the draft has no occupations. */
internal fun WMCache.Value.MixDraft.draftStatsSignature(): String? {
    return if (occupations.isEmpty()) null else statsSignature(statsRequest())
}

/** Each occupation's share of the mix, 0..1, for weighting the fetched collection; disabled occupations have 0. */
internal fun WMCache.Value.MixDraft.shares(): Map<Int, Double> = occupations.associate { it.id to shareOf(it) }

internal fun WMCache.Value.MixDraft.toFilters(): WMMixFilters = WMMixFilters.of(areaId, industryId)

/** The stored form: each occupation's share as a percentage, with the justification the stored mix had. */
internal fun WMCache.Value.MixDraft.toStoredOccupations(source: WMStoredMix?): List<WMFirebaseMix.Occupation> {
    val justifications = source?.occupations?.associate { it.id to it.justification } ?: emptyMap()
    return occupations.map { o ->
        WMFirebaseMix.Occupation.kxs(
            id = o.id,
            percentage = shareOf(o) * 100.0,
            justification = justifications[o.id] ?: "",
            isEnabled = o.enabled
        )
    }
}

/** A stored mix of [ownerUid] opened into the builder; disabled occupations keep a locked memory of their share. */
internal fun WMStoredMix.toDraft(ownerUid: String?): WMCache.Value.MixDraft {
    val enabledCount = occupations.count { it.isEnabled }.coerceAtLeast(1)
    return WMCache.Value.MixDraft.create(
        occupations = occupations.map { o ->
            WMCache.Value.DraftOccupation.create(
                id = o.id,
                weight = o.percentage / 100.0 * enabledCount * WEIGHT_UNIT,
                locked = !o.isEnabled,
                enabled = o.isEnabled
            )
        },
        areaId = filters.areaId,
        industryId = filters.industryId,
        sourceMixId = id,
        title = title,
        ownerUid = ownerUid
    )
}

/** Whether the draft differs from the stored mix it was opened from (within rounding). */
internal fun WMCache.Value.MixDraft.differsFrom(source: WMStoredMix): Boolean {
    if ((title ?: source.title) != source.title) {
        return true
    }
    if (areaId != source.filters.areaId || industryId != source.filters.industryId) {
        return true
    }
    val stored = source.occupations.associateBy { it.id }
    if (stored.keys != occupations.map { it.id }.toSet()) {
        return true
    }
    return occupations.any { o ->
        val s = stored.getValue(o.id)
        s.isEnabled != o.enabled || abs(shareOf(o) * 100.0 - (if (s.isEnabled) s.percentage else 0.0)) > 0.5
    }
}
